import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { ProductState } from '@platform-frontend/checkout';
import { BillingAddress, MARKETPLACE_MATOMO_EVENT_CATEGORY_BILLING } from '@platform-frontend/marketplace';
import { ClearNotifications, Navigate } from '@platform-frontend/util';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { NavigationService } from '../../navigation.service';
import { BillingIdWithCallback } from '../billing-id-with-callback';
import { NullableIdWithCallback } from '../nullable-id-with-callback';

@UntilDestroy()
@Component({
  selector: 'yat-billing-address-selection-common',
  templateUrl: './billing-address-selection-common.component.html',
  styleUrls: ['./billing-address-selection-common.component.scss']
})
export class BillingAddressSelectionCommonComponent implements OnInit {
  constructor(private activatedRoute: ActivatedRoute, private store: Store, private navigationService: NavigationService) {}

  private _sameBillingAddressSelected$ = new BehaviorSubject<boolean>(false);

  @Input()
  selectedBillingAddress$: Observable<BillingAddress | null> = of(null);

  @Input()
  billingAddresses$: Observable<BillingAddress[]> = of([]);

  @Input()
  updateSubscriptionWithContractId: string | null = null;

  @Output()
  saveSelection: EventEmitter<NullableIdWithCallback> = new EventEmitter();

  @Output()
  prepareDeleteAddress: EventEmitter<number> = new EventEmitter();

  @Output()
  deleteAddress: EventEmitter<BillingIdWithCallback> = new EventEmitter();

  @Select(ProductState.isLive)
  isLive$: Observable<boolean>;

  selectedId: number | null;

  readonly MATOMO_EVENT_CATEGORY_BILLING = MARKETPLACE_MATOMO_EVENT_CATEGORY_BILLING;

  ngOnInit(): void {
    this.selectedBillingAddress$
      .pipe(
        tap(selectedAddress => (this.selectedId = selectedAddress ? selectedAddress.id : null)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  get sameBillingAddressSelected$(): Observable<boolean> {
    return this._sameBillingAddressSelected$.asObservable();
  }

  trackBillingAddress(_: number, billingAddress: BillingAddress): number {
    return billingAddress.id;
  }

  select(id: number | null, completeCallback: () => void = () => undefined): void {
    if (this.selectedId !== id) {
      this._sameBillingAddressSelected$.next(false);
      this.saveSelection.emit({ id, completeCallback });
    } else {
      this._sameBillingAddressSelected$.next(true);
      completeCallback();
    }
  }

  editSelectedBillingAddress(billingAddress: BillingAddress): void {
    const queryParamsForEdit = {
      id: billingAddress.id
    };
    this.store.dispatch(new Navigate(['../billing-address'], { relativeTo: this.activatedRoute, queryParams: queryParamsForEdit }));
  }

  prepareDelete(id: number): void {
    setTimeout(() => this.prepareDeleteAddress.next(id));
  }

  cancelDelete(): void {
    this.store.dispatch(new ClearNotifications());
  }

  delete(id: number, completeCallback: () => void): void {
    this.deleteAddress.emit({ id, completeCallback });
  }

  modalClosed(): void {
    this._sameBillingAddressSelected$.next(false);
  }

  continue(): void {
    this.navigationService.gotoConfirmationOrOverview(this.activatedRoute).pipe(untilDestroyed(this)).subscribe();
  }
}
